'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import WorldwayNav from '@/components/WorldwayNav';

interface Destination {
  id: string;
  name: string;
  slug: string;
  country: string;
  region: string;
  continent: string;
  short_description: string;
  hero_image: string;
  tags: string[];
  popularity_score: number;
}

interface SearchState {
  vertical: string;
  destination: string;
  checkIn: string;
  checkOut: string;
  travelers: number;
  budget: string;
}

const TRAVEL_VERTICALS = [
{ id: 'flight', label: 'Flights', icon: '✈' },
{ id: 'hotel', label: 'Hotels', icon: '🏨' },
{ id: 'activity', label: 'Activities', icon: '🎯' },
{ id: 'transfer', label: 'Transfers', icon: '🚗' },
{ id: 'cruise', label: 'Cruises', icon: '🛳' },
{ id: 'private_aviation', label: 'Private Jets', icon: '🛩' },
{ id: 'safari', label: 'Safari', icon: '🦁' },
{ id: 'tailor_made', label: 'Tailor-Made', icon: '✨' },
{ id: 'villa', label: 'Villas & Estates', icon: '🏰' },
{ id: 'yacht', label: 'Yacht Charters', icon: '⛵' },
{ id: 'rail', label: 'Luxury Rail', icon: '🚂' },
{ id: 'polar', label: 'Polar Expeditions', icon: '🧊' }];


const STATS = [
{ value: '180+', label: 'Destinations' },
{ value: '12K+', label: 'Curated Journeys' },
{ value: '98%', label: 'Client Satisfaction' },
{ value: '24/7', label: 'Concierge Access' }];


const CURRENCIES = ['USD', 'EUR', 'GBP', 'AED', 'SGD', 'INR', 'AUD'];

const FEATURED_CRUISES = [
{ name: 'Regent Seven Seas Splendor', route: 'Caribbean · Miami round-trip', nights: '10 nights', line: 'Regent Seven Seas', price: 'From $9,800 per guest', guests: '750 guests', href: '/cruises' },
{ name: 'Silversea Mediterranean Classics', route: 'Rome to Venice', nights: '10 nights', line: 'Silversea', price: 'From $12,800 per guest', guests: '596 guests', href: '/cruises' },
{ name: 'Ritz-Carlton Yacht Collection — Rivieras', route: 'Barcelona to Rome', nights: '7 nights', line: 'Ritz-Carlton Yacht', price: 'From $13,500 per guest', guests: '298 guests', href: '/cruises' },
{ name: 'Seabourn Norwegian Fjords', route: 'Copenhagen to Bergen', nights: '14 nights', line: 'Seabourn', price: 'From $15,900 per guest', guests: '600 guests', href: '/cruises' }];


const FEATURED_SAFARIS = [
{ name: 'Serengeti Great Migration', location: 'Tanzania', nights: '10 nights, mobile camps', operator: 'Singita & Sanctuary', price: 'From $18,500 per guest', href: '/safari' },
{ name: 'Rwanda Mountain Gorillas', location: 'Rwanda', nights: '6 nights, luxury lodges', operator: 'Singita & One&Only', price: 'From $19,800 per guest', href: '/safari' },
{ name: 'Sabi Sand Big Five', location: 'South Africa', nights: '5 nights, luxury lodge', operator: 'Singita, Londolozi, Royal Malewane', price: 'From $14,600 per guest', href: '/safari' },
{ name: 'Okavango Delta Water Safari', location: 'Botswana', nights: '8 nights, exclusive camps', operator: 'Wilderness Safaris', price: 'From $22,400 per guest', href: '/safari' }];


const FEATURED_TAILOR_MADE = [
{ name: 'Italian Lakes & Amalfi Honeymoon', location: 'Italy', nights: '12 nights, private', price: 'From $22,800 per guest', href: '/tailor-made' },
{ name: 'Royal Rajasthan Private Tour', location: 'India', nights: '13 nights, private', price: 'From $24,800 per guest', href: '/tailor-made' },
{ name: 'Japan Family Cherry Blossom', location: 'Japan', nights: '14 nights, private', price: 'From $38,400 per guest', href: '/tailor-made' },
{ name: 'Southern Africa Private Fly-In Safari', location: 'Botswana, Zambia, South Africa', nights: '14 nights, private', price: 'From $46,500 per guest', href: '/tailor-made' }];


const FEATURED_RAIL = [
{ name: 'Venice Simplon-Orient-Express', route: 'London — Paris — Venice', nights: '2 nights', operator: 'Belmond', price: 'From $5,400 per guest', href: '/rail' },
{ name: 'Rovos Rail — Pride of Africa', route: 'Cape Town to Dar es Salaam', nights: '15 nights', operator: 'Rovos Rail', price: 'From $18,500 per guest', href: '/rail' },
{ name: 'Maharajas\' Express — Heritage of India', route: 'Delhi to Mumbai', nights: '7 nights', operator: 'IRCTC', price: 'From $9,600 per guest', href: '/rail' },
{ name: 'Seven Stars in Kyushu', route: 'Hakata round-trip', nights: '3 nights', operator: 'JR Kyushu', price: 'From $11,800 per guest', href: '/rail' }];


const MEMBERSHIP_PLANS = [
{
  name: 'Traveler',
  price: 'Free',
  period: 'forever',
  badge: '',
  features: ['Free account & personal dashboard', 'Booking management', 'Wishlist & saved itineraries', 'Book Flights, Hotels, Activities, Buses, Cruises, Rail, Transfers', 'Unlimited search after registration'],
  cta: 'Start Free',
  href: '/auth/register',
  highlight: false
},
{
  name: 'Travel Plus',
  price: '$199',
  period: 'per year',
  badge: '',
  features: ['All Traveler benefits', 'Member-only pricing & exclusive offers', 'Priority customer support', 'Personalized recommendations', 'Exclusive travel promotions', 'Loyalty rewards & partner benefits'],
  cta: 'Choose Travel Plus',
  href: '/auth/register',
  highlight: false
},
{
  name: 'Elite',
  price: '$299–399',
  period: 'per year',
  badge: 'Most Popular',
  features: ['All Travel Plus benefits', 'AI Concierge access', 'AI Trip Builder', 'VIP luxury travel concierge', 'Priority booking assistance', 'Personalized itinerary planning', 'Exclusive luxury experiences', 'Early access to premium offers', 'Highest-priority support'],
  cta: 'Upgrade to Elite',
  href: '/auth/register',
  highlight: true
},
{
  name: 'Elite Plus',
  price: '$599',
  period: 'per year',
  badge: '',
  features: ['All Elite benefits', 'Unlimited AI Concierge usage', 'Unlimited AI Trip Builder', 'Priority GPU routing for faster answers', 'Dedicated luxury travel desk', 'Complimentary itinerary reviews', 'Early-access invitations & upgrades'],
  cta: 'Upgrade to Elite Plus',
  href: '/auth/register',
  highlight: false
}];


export default function HomePage() {
  const router = useRouter();
  const [activeDestIdx, setActiveDestIdx] = useState(0);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState<SearchState>({
    vertical: 'flight',
    destination: '',
    checkIn: '',
    checkOut: '',
    travelers: 2,
    budget: ''
  });

  const fallbackDestinations: Destination[] = [
  { id: '1', name: 'Maldives', slug: 'maldives', country: 'Maldives', region: 'Indian Ocean', continent: 'Asia', short_description: 'Pristine overwater villas and crystal-clear lagoons', hero_image: 'https://images.unsplash.com/photo-1649323915158-cf6beb7b3d96', tags: ['luxury', 'beach', 'honeymoon'], popularity_score: 98 },
  { id: '2', name: 'Santorini', slug: 'santorini', country: 'Greece', region: 'Cyclades', continent: 'Europe', short_description: 'Iconic white-washed buildings and caldera views', hero_image: 'https://images.unsplash.com/photo-1663431262855-ec53c297a3af', tags: ['luxury', 'romance', 'sunset'], popularity_score: 96 },
  { id: '3', name: 'Dubai', slug: 'dubai', country: 'UAE', region: 'Dubai Emirate', continent: 'Asia', short_description: 'Ultramodern luxury and desert adventures', hero_image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a04ef789-1785695313491.png', tags: ['luxury', 'shopping', 'desert'], popularity_score: 97 },
  { id: '4', name: 'Kyoto', slug: 'kyoto', country: 'Japan', region: 'Kansai', continent: 'Asia', short_description: 'Ancient temples and traditional Japanese culture', hero_image: 'https://images.unsplash.com/photo-1708527731834-419bf5fe1d5b', tags: ['culture', 'temples', 'zen'], popularity_score: 92 },
  { id: '5', name: 'Amalfi Coast', slug: 'amalfi-coast', country: 'Italy', region: 'Campania', continent: 'Europe', short_description: 'Dramatic cliffs and charming coastal villages', hero_image: 'https://images.unsplash.com/photo-1493105374331-dfbd31af4c21', tags: ['luxury', 'coastal', 'culture'], popularity_score: 95 },
  { id: '6', name: 'Bora Bora', slug: 'bora-bora', country: 'French Polynesia', region: 'Society Islands', continent: 'Oceania', short_description: 'Turquoise lagoons and overwater bungalows', hero_image: 'https://images.unsplash.com/photo-1662792721650-545a15f07ff6', tags: ['luxury', 'beach', 'snorkeling'], popularity_score: 94 }];


  useEffect(() => {
    const interval = setInterval(() => {
      setActiveDestIdx((prev) => (prev + 1) % fallbackDestinations.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetch('/api/destinations').
    then((r) => r.json()).
    then((d) => {if (d?.data?.length) setDestinations(d.data);}).
    catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayDestinations = destinations.length > 0 ? destinations : fallbackDestinations;

  const handleAiSearch = useCallback(async () => {
    if (!search.destination) return;
    setAiLoading(true);
    setAiSuggestion('');
    try {
      const res = await fetch('/api/ai/concierge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `Suggest luxury ${search.vertical} options for ${search.destination} for ${search.travelers} travelers. Budget: ${search.budget || 'flexible'}. Travel dates: ${search.checkIn || 'flexible'}.`,
          sessionId: 'homepage-search'
        })
      });
      if (res.ok) {
        const reader = res.body?.getReader();
        const decoder = new TextDecoder();
        let text = '';
        while (reader) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.content) text += data.content;
              } catch {}
            }
          }
          setAiSuggestion(text);
        }
      }
    } catch {}
    setAiLoading(false);
  }, [search]);

  const handleSearch = () => {
    const params = new URLSearchParams({
      vertical: search.vertical,
      destination: search.destination,
      checkIn: search.checkIn,
      checkOut: search.checkOut,
      travelers: String(search.travelers),
      budget: search.budget,
      currency
    });
    router.push(`/booking?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)] overflow-x-hidden">
      {/* ── WorldwayNav — full navigation matching worldwaytravelsgroup.com ── */}
      <WorldwayNav />

      {/* ── Hero ── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.12) 0%, transparent 70%)' }} />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)', filter: 'blur(40px)' }} />

        <div className="relative z-10 text-center max-w-6xl mx-auto px-6 pt-24 w-full">
          <div className="inline-flex items-center gap-2 border border-[rgba(201,168,76,0.3)] rounded-full px-4 py-1.5 mb-8 text-xs tracking-widest uppercase text-[var(--primary)]" style={{ background: 'rgba(201,168,76,0.06)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)] animate-pulse" />
            Private Aviation · Ocean Residences · Curated Experiences
          </div>

          <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
            <span className="block text-[var(--foreground)]">The World, Arranged</span>
            <span className="block gold-text-gradient">In a Single, Quiet Gesture.</span>
          </h1>

          <p className="text-lg md:text-xl text-[var(--muted-foreground)] max-w-2xl mx-auto mb-10 leading-relaxed">
            Private aviation, ocean-view residences, and curated experiences — orchestrated by a 24/7 concierge, human and AI.
          </p>

          {/* ── Booking Search Widget ── */}
          <div className="glass-card rounded-2xl p-6 mb-10 text-left max-w-5xl mx-auto border border-[rgba(201,168,76,0.15)]">
            {/* Header row: dropdown + label */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] tracking-widest uppercase text-[var(--muted-foreground)]">Select Travel Category</p>
              {/* Dropdown selector */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border border-[rgba(201,168,76,0.3)] text-[var(--primary)] hover:bg-[rgba(201,168,76,0.08)] transition-all">
                  <span>{TRAVEL_VERTICALS.find((v) => v.id === search.vertical)?.icon}</span>
                  <span>{TRAVEL_VERTICALS.find((v) => v.id === search.vertical)?.label}</span>
                  <svg className={`w-3 h-3 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {dropdownOpen &&
                <div className="absolute right-0 top-full mt-1 w-52 rounded-xl border border-[var(--border)] bg-[var(--surface-2)] shadow-2xl z-50 py-1 overflow-hidden">
                    {TRAVEL_VERTICALS.map((v) =>
                  <button
                    key={v.id}
                    onClick={() => {setSearch((s) => ({ ...s, vertical: v.id }));setDropdownOpen(false);}}
                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-xs text-left transition-colors ${search.vertical === v.id ? 'bg-[rgba(201,168,76,0.12)] text-[var(--primary)]' : 'text-[var(--muted-foreground)] hover:bg-[var(--surface-3)] hover:text-[var(--foreground)]'}`}>
                        <span className="text-base">{v.icon}</span>
                        <span className="font-medium">{v.label}</span>
                        {search.vertical === v.id &&
                    <svg className="w-3 h-3 ml-auto text-[var(--primary)]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                    }
                      </button>
                  )}
                  </div>
                }
              </div>
            </div>

            {/* Vertical tabs — wrap so all are visible */}
            <div className="flex flex-wrap gap-1.5 pb-3 mb-5">
              {TRAVEL_VERTICALS.map((v) =>
              <button
                key={v.id}
                onClick={() => setSearch((s) => ({ ...s, vertical: v.id }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${search.vertical === v.id ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--surface-3)]'}`}>
                  <span>{v.icon}</span>
                  {v.label}
                </button>
              )}
            </div>

            {/* Search fields */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="md:col-span-2">
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">
                  {search.vertical === 'flight' ? 'From / To' : 'Destination'}
                </label>
                <input
                  type="text"
                  placeholder={search.vertical === 'flight' ? 'City or airport' : 'Where to?'}
                  value={search.destination}
                  onChange={(e) => setSearch((s) => ({ ...s, destination: e.target.value }))}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]" />
                
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">
                  {search.vertical === 'flight' ? 'Departure' : 'Check In'}
                </label>
                <input
                  type="date"
                  value={search.checkIn}
                  onChange={(e) => setSearch((s) => ({ ...s, checkIn: e.target.value }))}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]" />
                
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">
                  {search.vertical === 'flight' ? 'Return' : 'Check Out'}
                </label>
                <input
                  type="date"
                  value={search.checkOut}
                  onChange={(e) => setSearch((s) => ({ ...s, checkOut: e.target.value }))}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]" />
                
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">
                  {search.vertical === 'flight' ? 'Passengers' : 'Guests'}
                </label>
                <select
                  value={search.travelers}
                  onChange={(e) => setSearch((s) => ({ ...s, travelers: Number(e.target.value) }))}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  {[1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20].map((n) =>
                  <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                  )}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <input
                type="text"
                placeholder="Budget (optional)"
                value={search.budget}
                onChange={(e) => setSearch((s) => ({ ...s, budget: e.target.value }))}
                className="flex-1 bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]" />
              
              <button
                onClick={handleAiSearch}
                disabled={aiLoading || !search.destination}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border border-[rgba(201,168,76,0.3)] text-[var(--primary)] hover:bg-[rgba(201,168,76,0.08)] transition-all disabled:opacity-40">
                <span>✨</span>
                {aiLoading ? 'AI Thinking...' : 'Ask Concierge'}
              </button>
              <button onClick={handleSearch} className="btn-primary px-8 py-2.5 rounded-lg text-sm font-semibold">
                Search
              </button>
            </div>

            {aiSuggestion &&
            <div className="mt-4 p-4 rounded-xl border border-[rgba(201,168,76,0.2)] bg-[rgba(201,168,76,0.04)] text-sm text-[var(--foreground)] leading-relaxed">
                <div className="flex items-center gap-2 mb-2 text-[var(--primary)] text-xs font-medium tracking-widest uppercase">
                  <span>✨</span> AI Concierge Suggestion
                </div>
                {aiSuggestion}
              </div>
            }
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto">
            {STATS.map((s) =>
            <div key={s.label} className="text-center">
                <div className="text-2xl font-bold text-[var(--primary)]" style={{ fontFamily: 'var(--font-serif)' }}>{s.value}</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-1 tracking-wide uppercase">{s.label}</div>
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[var(--muted-foreground)] text-xs">
          <span className="tracking-widest uppercase">Explore</span>
          <div className="w-px h-8 bg-gradient-to-b from-[var(--primary)] to-transparent" />
        </div>
      </section>

      {/* ── The Atelier / Services ── */}
      <section id="services" className="py-20 border-t border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <p className="label-caps mb-2">The Atelier</p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
              Eight Ways to<br />
              <span className="gold-text-gradient">Travel Exquisitely</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
            { icon: '✈', label: 'Flights', desc: 'First-class itineraries, quietly arranged. One-way, round-trip and multi-city.', href: '/flights' },
            { icon: '🏨', label: 'Hotels & Residences', desc: 'Curated palaces, villas, and design hotels around the world.', href: '/hotels' },
            { icon: '🎯', label: 'Activities', desc: 'Private after-hours museums, chef tables, heli-skiing, and beyond.', href: '/activities' },
            { icon: '🚗', label: 'Transfers', desc: 'Kerb to cabin, seamlessly. Chauffeured saloons, SUVs, and armored fleets.', href: '/transfers' },
            { icon: '🛩', label: 'Private Jets', desc: 'Wheels up, on your schedule. Instant quotes across the world\'s finest private aircraft.', href: '/private-jets' },
            { icon: '🛳', label: 'Luxury Cruises', desc: 'All-suite ships, Michelin-caliber dining and immersive shore programmes.', href: '/cruises' },
            { icon: '🚌', label: 'Coach & Bus', desc: 'Move your group in comfort. Executive coaches, minibuses, and private group fleets.', href: '/booking?vertical=bus' },
            { icon: '✨', label: 'AI Concierge', desc: 'Ask anything. Anywhere. Anytime. A discreet, always-on intelligence.', href: '/ai-concierge' }].
            map((v) =>
            <Link
              key={v.label}
              href={v.href}
              className="glass-card rounded-2xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group">
                <div className="text-3xl mb-3">{v.icon}</div>
                <h3 className="text-base font-bold mb-2 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{v.label}</h3>
                <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{v.desc}</p>
                <div className="text-xs text-[var(--primary)] mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Explore →</div>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ── Collections ── */}
      <section id="collections" className="py-20 border-t border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <p className="label-caps mb-2">Signature Collections</p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
              Extraordinary Journeys,<br />
              <span className="gold-text-gradient">Perfectly Curated</span>
            </h2>
          </div>

          {/* Luxury Cruises */}
          <div className="mb-16">
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="label-caps mb-1 text-[var(--primary)]">All-inclusive at sea</p>
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Luxury Cruises</h3>
                <p className="text-sm text-[var(--muted-foreground)] mt-1 max-w-xl">All-suite ships, Michelin-caliber dining and immersive shore programmes with Silversea, Regent Seven Seas, Seabourn, Explora Journeys and Ritz-Carlton Yacht Collection.</p>
              </div>
              <Link href="/cruises" className="hidden md:block text-sm text-[var(--primary)] hover:underline whitespace-nowrap">View all voyages →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {FEATURED_CRUISES.map((c) =>
              <Link key={c.name} href={c.href} className="glass-card rounded-xl p-4 hover:border-[rgba(201,168,76,0.3)] transition-all group">
                  <div className="text-2xl mb-2">🛳</div>
                  <h4 className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{c.name}</h4>
                  <p className="text-xs text-[var(--muted-foreground)] mb-1">{c.route}</p>
                  <p className="text-xs text-[var(--muted-foreground)] mb-2">{c.nights} · {c.guests}</p>
                  <p className="text-xs font-semibold text-[var(--primary)]">{c.price}</p>
                </Link>
              )}
            </div>
          </div>

          {/* Safari Experiences */}
          <div className="mb-16">
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="label-caps mb-1 text-[var(--primary)]">The wild in luxury</p>
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Safari Experiences</h3>
                <p className="text-sm text-[var(--muted-foreground)] mt-1 max-w-xl">Africa&apos;s most storied private conservancies and mobile camps — the Great Migration, mountain gorillas, Okavango water safaris and Cape Winelands, hosted by master guides.</p>
              </div>
              <Link href="/safari" className="hidden md:block text-sm text-[var(--primary)] hover:underline whitespace-nowrap">View all safaris →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {FEATURED_SAFARIS.map((s) =>
              <Link key={s.name} href={s.href} className="glass-card rounded-xl p-4 hover:border-[rgba(201,168,76,0.3)] transition-all group">
                  <div className="text-2xl mb-2">🦁</div>
                  <h4 className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{s.name}</h4>
                  <p className="text-xs text-[var(--muted-foreground)] mb-1">{s.location}</p>
                  <p className="text-xs text-[var(--muted-foreground)] mb-1">{s.nights}</p>
                  <p className="text-xs text-[var(--muted-foreground)] mb-2">{s.operator}</p>
                  <p className="text-xs font-semibold text-[var(--primary)]">{s.price}</p>
                </Link>
              )}
            </div>
          </div>

          {/* Tailor-Made Journeys */}
          <div className="mb-16">
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="label-caps mb-1 text-[var(--primary)]">Written for you</p>
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Tailor-Made Journeys</h3>
                <p className="text-sm text-[var(--muted-foreground)] mt-1 max-w-xl">Every element crafted around you — your interests, your travel companions, your pace. Speak with a specialist and receive a bespoke itinerary within 72 hours.</p>
              </div>
              <Link href="/tailor-made" className="hidden md:block text-sm text-[var(--primary)] hover:underline whitespace-nowrap">View all journeys →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {FEATURED_TAILOR_MADE.map((t) =>
              <Link key={t.name} href={t.href} className="glass-card rounded-xl p-4 hover:border-[rgba(201,168,76,0.3)] transition-all group">
                  <div className="text-2xl mb-2">✨</div>
                  <h4 className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{t.name}</h4>
                  <p className="text-xs text-[var(--muted-foreground)] mb-1">{t.location}</p>
                  <p className="text-xs text-[var(--muted-foreground)] mb-2">{t.nights}</p>
                  <p className="text-xs font-semibold text-[var(--primary)]">{t.price}</p>
                </Link>
              )}
            </div>
          </div>

          {/* Luxury Rail */}
          <div className="mb-16">
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="label-caps mb-1 text-[var(--primary)]">Grand rail voyages</p>
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Luxury Rail Journeys</h3>
                <p className="text-sm text-[var(--muted-foreground)] mt-1 max-w-xl">The romance of the golden age of rail, reimagined — Belmond&apos;s Venice Simplon-Orient-Express, Royal Scotsman, Rocky Mountaineer, Rovos Rail, Maharajas&apos; Express and Japan&apos;s Seven Stars.</p>
              </div>
              <Link href="/rail" className="hidden md:block text-sm text-[var(--primary)] hover:underline whitespace-nowrap">View all rail journeys →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {FEATURED_RAIL.map((r) =>
              <Link key={r.name} href={r.href} className="glass-card rounded-xl p-4 hover:border-[rgba(201,168,76,0.3)] transition-all group">
                  <div className="text-2xl mb-2">🚂</div>
                  <h4 className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{r.name}</h4>
                  <p className="text-xs text-[var(--muted-foreground)] mb-1">{r.route}</p>
                  <p className="text-xs text-[var(--muted-foreground)] mb-1">{r.nights} · {r.operator}</p>
                  <p className="text-xs font-semibold text-[var(--primary)]">{r.price}</p>
                </Link>
              )}
            </div>
          </div>

          {/* Villas & Yachts row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass-card rounded-2xl p-6 border border-[rgba(201,168,76,0.1)]">
              <p className="label-caps mb-1 text-[var(--primary)]">Whole-house rentals</p>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>Villas & Estates</h3>
              <p className="text-sm text-[var(--muted-foreground)] mb-4">Fully staffed private villas, historic estates and beachfront residences worldwide — from Tuscan castellos to St. Barths beachfront to Balinese cliff-top compounds.</p>
              <div className="space-y-2 mb-4">
                {[
                { name: 'Castello di Tuscany', detail: 'Tuscany, Italy · 12-bedroom private castle', price: 'From $78,000/week' },
                { name: 'Bali Cliff-Top Estate', detail: 'Bali, Indonesia · 8-bedroom Uluwatu compound', price: 'From $24,000/week' },
                { name: 'St Barths Beachfront Villa', detail: 'St Barths, Caribbean · 6-bedroom oceanfront', price: 'From $42,000/week' }].
                map((v) =>
                <div key={v.name} className="flex items-center justify-between text-xs border-b border-[var(--border)] pb-2">
                    <div>
                      <span className="text-[var(--foreground)] font-medium">{v.name}</span>
                      <span className="text-[var(--muted-foreground)] ml-2">{v.detail}</span>
                    </div>
                    <span className="text-[var(--primary)] font-semibold whitespace-nowrap ml-2">{v.price}</span>
                  </div>
                )}
              </div>
              <Link href="/villas" className="text-sm text-[var(--primary)] hover:underline">View all villas →</Link>
            </div>

            <div className="glass-card rounded-2xl p-6 border border-[rgba(201,168,76,0.1)]">
              <p className="label-caps mb-1 text-[var(--primary)]">The blue horizon</p>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>Yacht Charters</h3>
              <p className="text-sm text-[var(--muted-foreground)] mb-4">Motor and sailing yacht charters worldwide — Mediterranean summers, Caribbean winters, the Galápagos, Indonesia and the South Pacific — with full crew, chef and toys.</p>
              <div className="space-y-2 mb-4">
                {[
                { name: '72m Motor Yacht — Mediterranean', detail: 'Mediterranean · 12 guests, 20 crew', price: 'From $750,000/week' },
                { name: 'Indonesia Phinisi Charter', detail: 'Indonesia · 8 guests, traditional design', price: 'From $68,000/week' },
                { name: '60m Sailing Yacht — Caribbean', detail: 'Caribbean · 10 guests, luxury sailing', price: 'From $320,000/week' }].
                map((y) =>
                <div key={y.name} className="flex items-center justify-between text-xs border-b border-[var(--border)] pb-2">
                    <div>
                      <span className="text-[var(--foreground)] font-medium">{y.name}</span>
                      <span className="text-[var(--muted-foreground)] ml-2">{y.detail}</span>
                    </div>
                    <span className="text-[var(--primary)] font-semibold whitespace-nowrap ml-2">{y.price}</span>
                  </div>
                )}
              </div>
              <Link href="/yachts" className="text-sm text-[var(--primary)] hover:underline">View all yachts →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Destinations ── */}
      <section id="destinations" className="py-20 border-t border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="label-caps mb-2">Curated Destinations</p>
              <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
                Where Luxury<br />
                <span className="gold-text-gradient">Meets Discovery</span>
              </h2>
            </div>
            <Link href="/booking" className="hidden md:block text-sm text-[var(--primary)] hover:underline">
              View all 180+ destinations →
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {displayDestinations.slice(0, 6).map((d, i) =>
            <Link
              key={d.id}
              href={`/booking?destination=${d.name}&vertical=hotel`}
              onMouseEnter={() => setActiveDestIdx(i)}
              className={`relative glass-card rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 group ${activeDestIdx === i ? 'border-[rgba(201,168,76,0.4)] gold-glow' : ''}`}
              style={{ minHeight: '200px' }}>
                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" style={{ backgroundImage: `url(${d.hero_image})` }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050810]/90 via-[#050810]/40 to-transparent" />
                <div className="relative z-10 p-5 flex flex-col justify-end h-full" style={{ minHeight: '200px' }}>
                  <div className="label-caps mb-1 text-[var(--primary)]">{d.country}</div>
                  <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-serif)' }}>{d.name}</h3>
                  <p className="text-xs text-[var(--muted-foreground)] line-clamp-2">{d.short_description}</p>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {(d.tags || []).slice(0, 3).map((tag) =>
                  <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.15)] text-[var(--primary)]">{tag}</span>
                  )}
                  </div>
                </div>
                <div className={`absolute top-4 right-4 w-2 h-2 rounded-full transition-all duration-300 ${activeDestIdx === i ? 'bg-[var(--primary)] shadow-[0_0_8px_rgba(201,168,76,0.6)]' : 'bg-[var(--border)]'}`} />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ── AI Concierge CTA ── */}
      <section className="py-20 border-t border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="glass-card rounded-3xl p-10 border border-[rgba(201,168,76,0.2)] text-center" style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.04) 0%, rgba(10,15,30,0.8) 100%)' }}>
            <div className="text-4xl mb-4">✨</div>
            <p className="label-caps mb-3">AI Concierge</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
              Ask Anything.<br />
              <span className="gold-text-gradient">Anywhere. Anytime.</span>
            </h2>
            <p className="text-[var(--muted-foreground)] mb-6 max-w-xl mx-auto">A discreet, always-on intelligence for travel arrangements. Good day. I&apos;m your Worldway concierge — where shall we begin?</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8 text-left max-w-2xl mx-auto">
              {[
              'Plan a 5-day romantic escape to the Amalfi coast in September',
              'Private jet options from Delhi to the Maldives next weekend',
              'Best time and villa for a family safari in Kenya',
              'Curate a Michelin dining itinerary in Tokyo for 3 nights'].
              map((prompt) =>
              <Link key={prompt} href={`/ai-concierge?q=${encodeURIComponent(prompt)}`} className="text-xs text-[var(--muted-foreground)] hover:text-[var(--primary)] transition-colors p-3 rounded-lg border border-[var(--border)] hover:border-[rgba(201,168,76,0.3)]">
                  &ldquo;{prompt}&rdquo;
                </Link>
              )}
            </div>
            <Link href="/ai-concierge" className="btn-primary px-10 py-4 rounded-xl text-base font-semibold shadow-[0_4px_24px_rgba(201,168,76,0.3)]">
              Open AI Concierge
            </Link>
          </div>
        </div>
      </section>

      {/* ── Membership ── */}
      <section id="membership" className="py-24 border-t border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="label-caps mb-4">Membership</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
              Choose How<br />
              <span className="gold-text-gradient">You Travel</span>
            </h2>
            <p className="text-[var(--muted-foreground)] max-w-xl mx-auto">From complimentary access to unlimited AI-powered luxury planning.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {MEMBERSHIP_PLANS.map((plan) =>
            <div key={plan.name} className={`glass-card rounded-2xl p-6 flex flex-col relative ${plan.highlight ? 'border-[rgba(201,168,76,0.5)] gold-glow' : 'border-[var(--border)]'}`}>
                {plan.badge &&
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--primary)] text-[var(--primary-foreground)] text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                    {plan.badge}
                  </div>
              }
                <div className="mb-4">
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-serif)' }}>{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-[var(--primary)]">{plan.price}</span>
                    <span className="text-xs text-[var(--muted-foreground)]">{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-2 flex-1 mb-6">
                  {plan.features.map((f) =>
                <li key={f} className="flex items-start gap-2 text-xs text-[var(--muted-foreground)]">
                      <span className="text-[var(--primary)] mt-0.5 flex-shrink-0">✓</span>
                      {f}
                    </li>
                )}
                </ul>
                <Link href={plan.href} className={`text-center py-2.5 rounded-lg text-sm font-semibold transition-all ${plan.highlight ? 'btn-primary' : 'btn-ghost border border-[var(--border)]'}`}>
                  {plan.cta}
                </Link>
              </div>
            )}
          </div>

          <p className="text-center text-xs text-[var(--muted-foreground)] mt-6">
            Existing Enterprise, Agency, Company, HNI, UHNI, Corporate, White Label, and Partner memberships remain unchanged.{' '}
            <Link href="/booking" className="text-[var(--primary)] hover:underline">Learn more</Link>
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-[var(--border)] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <div className="text-[var(--primary)] font-bold tracking-widest uppercase mb-3" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</div>
              <p className="text-xs text-[var(--muted-foreground)] leading-relaxed mb-3">Discreet, bespoke luxury travel — curated flights, residences, and experiences on six continents.</p>
              <div className="text-xs text-[var(--muted-foreground)] space-y-1">
                <div>concierge@worldwaytravelsgroup.com</div>
                <div>+1 (800) 000-0000</div>
                <div className="text-[var(--primary)]">Available 24 / 7 / 365</div>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-[var(--foreground)] mb-4">Travel</div>
              <div className="space-y-2">
                {[
                { label: 'Private Jets', href: '/private-jets' },
                { label: 'Flights', href: '/flights' },
                { label: 'Hotels & Residences', href: '/hotels' },
                { label: 'Activities', href: '/activities' },
                { label: 'Villas & Estates', href: '/villas' },
                { label: 'Yacht Charters', href: '/yachts' }].
                map((l) =>
                <Link key={l.label} href={l.href} className="block text-xs text-[var(--muted-foreground)] hover:text-[var(--primary)] transition-colors">{l.label}</Link>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-[var(--foreground)] mb-4">Collections</div>
              <div className="space-y-2">
                {[
                { label: 'Luxury Cruises', href: '/cruises' },
                { label: 'Luxury Rail', href: '/rail' },
                { label: 'Safari', href: '/safari' },
                { label: 'Polar Expeditions', href: '/polar-expeditions' },
                { label: 'Tailor-Made', href: '/tailor-made' },
                { label: 'Travel Insurance', href: '/booking?vertical=insurance' }].
                map((l) =>
                <Link key={l.label} href={l.href} className="block text-xs text-[var(--muted-foreground)] hover:text-[var(--primary)] transition-colors">{l.label}</Link>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-[var(--foreground)] mb-4">Platform</div>
              <div className="space-y-2">
                {[
                { label: 'Customer Portal', href: '/customer-portal' },
                { label: 'Supplier Portal', href: '/supplier-portal' },
                { label: 'AI Concierge', href: '/ai-concierge' },
                { label: 'Enterprise Admin', href: '/enterprise-admin-platform' },
                { label: 'Transfers', href: '/transfers' }].
                map((l) =>
                <Link key={l.label} href={l.href} className="block text-xs text-[var(--muted-foreground)] hover:text-[var(--primary)] transition-colors">{l.label}</Link>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-[var(--foreground)] mb-4">Company</div>
              <div className="space-y-2">
                {['About Us', 'Trust & Safety', 'Help Centre', 'Contact', 'Privacy', 'Terms', 'Cookies'].map((s) =>
                <div key={s} className="text-xs text-[var(--muted-foreground)] hover:text-[var(--primary)] transition-colors cursor-pointer">{s}</div>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-[var(--border)] pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
            <div className="flex items-center gap-4 text-xs text-[var(--muted-foreground)]">
              <span>🔒 PCI DSS Compliant</span>
              <span>🛡 SSL Secured</span>
              <span>✈ IATA Certified</span>
            </div>
          </div>
        </div>
      </footer>
    </div>);

}